import React from 'react';
import { QrCode, Check, MapPin, Eye, Sparkles, ArrowRight, Compass, ChevronRight } from 'lucide-react';
import { Artifact } from '../types';

const GUIDE_STEPS = [
  {
    step: '01',
    phase: 'BƯỚC 1 · ĐỊNH VỊ',
    title: 'Chọn điểm dừng & Tới phòng tranh',
    desc: 'Mở ô tem tiếp theo trên ứng dụng để nhận gợi ý lộ trình, sau đó di chuyển đến không gian trưng bày tương ứng (Tầng 1 hoặc Tầng 2).',
    badge: 'Tầng 1 & Tầng 2 VNFAM',
    icon: MapPin,
    iconColor: 'text-[#f59e0b]',
    iconBg: 'bg-[#f59e0b]/15 border-[#f59e0b]/40',
  },
  {
    step: '02',
    phase: 'BƯỚC 2 · QUAN SÁT',
    title: 'Đối chiếu Bảo vật Quốc gia',
    desc: 'Quan sát tác phẩm thực tế, kiểm tra tên tác giả và số hiệu Bảo vật Quốc gia trên biển chú giải để đảm bảo bạn đứng đúng hiện vật.',
    badge: 'Bảng chú giải di sản',
    icon: Eye,
    iconColor: 'text-[#34d399]',
    iconBg: 'bg-[#10b981]/15 border-[#34d399]/40',
  },
  {
    step: '03',
    phase: 'BƯỚC 3 · XÁC THỰC',
    title: 'Mở camera & Quét mã QR',
    desc: 'Bật máy ảnh trực tiếp trên web và hướng vào mã QR dán tại chân đế hoặc bảng thông tin hiện vật để mở khóa.',
    badge: 'Mã QR tại chân hiện vật',
    icon: QrCode,
    iconColor: 'text-[#e6ba55]',
    iconBg: 'bg-[#e6ba55]/15 border-[#e6ba55]/40',
    hasScannerCta: true,
  },
  {
    step: '04',
    phase: 'BƯỚC 4 · GHI NHẬN',
    title: 'Nhận tem số & Tiếp tục hành trình',
    desc: 'Tem số được đóng dấu tức thì vào Sổ tem cá nhân, ghi nhận thành tích khám phá và mở ra manh mối cho điểm dừng tiếp theo.',
    badge: '+1 Tem số vào Bộ sưu tập',
    icon: Sparkles,
    iconColor: 'text-[#f472b6]',
    iconBg: 'bg-[#ec4899]/15 border-[#f472b6]/40',
  },
];

interface CollectionViewProps {
  artifacts: Artifact[];
  onOpenArtifact: (artifact: Artifact) => void;
  onOpenScanner: () => void;
}

export const CollectionView: React.FC<CollectionViewProps> = ({
  artifacts,
  onOpenArtifact,
  onOpenScanner,
}) => {
  const collectedCount = artifacts.filter((a) => a.unlocked).length;
  const totalCount = artifacts.length;
  const percent = Math.round((collectedCount / totalCount) * 100);

  // Next locked artifact for mission
  const nextTarget = artifacts.find((a) => !a.unlocked) || artifacts[0];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12 space-y-12 animate-fade-in pb-20">
      {/* COLLECTION HERO */}
      <section className="relative min-h-[360px] sm:min-h-[420px] rounded-2xl overflow-hidden border border-[#d2af66]/60 shadow-2xl flex items-center p-6 sm:p-12 text-[#fff4dd] bg-[#22181d]">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/museum/web/interior-gallery-lacquer-and-sculpture.webp"
            alt="Bộ sưu tập Quốc bảo"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#170d13] via-[#21171c]/90 to-[#21171c]/60" />
        </div>

        <div className="relative z-10 max-w-xl space-y-4">
          <span className="text-[#e6ba55] text-xs font-bold uppercase tracking-widest block">
            BỘ SƯU TẬP ĐANG MỞ
          </span>
          <h1 className="text-3xl sm:text-5xl font-serif font-black text-white leading-tight">
            Bộ Quốc bảo
          </h1>
          <p className="text-sm sm:text-base text-[#fff1dc]/85 leading-relaxed">
            Đến đúng hiện vật tại Bảo tàng và quét mã QR dán tại từng hạng mục. Mỗi mã hợp lệ sẽ mở một con tem số tương ứng vào cuốn sổ khám phá của bạn.
          </p>

          {/* Stats Pills */}
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <span className="px-3 py-1.5 rounded-full bg-[#24483f] text-[#d8f5e9] text-xs font-bold border border-[#5b9e8b] flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5 stroke-[3]" />
              {collectedCount} tem đã nhận
            </span>
            <span className="px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md text-[#fff4dd] text-xs font-semibold border border-white/20">
              {totalCount - collectedCount} điểm còn lại
            </span>
            <span className="px-3 py-1.5 rounded-full bg-[#e6ba55] text-[#402d0c] text-xs font-bold">
              {percent}% Bộ Quốc bảo
            </span>
          </div>

          <div className="pt-3">
            <button
              onClick={onOpenScanner}
              className="gf-button text-sm py-3 px-6 shadow-xl flex items-center gap-2"
            >
              <QrCode className="w-4 h-4" />
              <span>Mở camera quét QR</span>
            </button>
          </div>
        </div>
      </section>

      {/* COLLECTION SWITCHER */}
      <div className="collection-switcher">
        <button type="button" aria-pressed="true">
          <strong>Quốc bảo</strong>
          <span>Đang mở · {collectedCount}/{totalCount} tem mẫu</span>
        </button>
        <button type="button" disabled className="opacity-50 cursor-not-allowed">
          <strong>Tranh sơn mài</strong>
          <span>Coming soon</span>
        </button>
        <button type="button" disabled className="opacity-50 cursor-not-allowed">
          <strong>Điêu khắc Việt Nam</strong>
          <span>Coming soon</span>
        </button>
      </div>

      {/* CURRENT MISSION */}
      <section className="scan-mission">
        <div className="scan-mission__visual">
          <img
            src="/images/museum/web/interior-gallery-buddhist-sculpture.webp"
            alt="Phòng trưng bày hiện vật"
          />
          <span>Điểm dừng 0{nextTarget.id + 1} / 0{totalCount} · Thử thách</span>
        </div>

        <div className="scan-mission__copy">
          <span className="text-xs font-bold text-[#e6ba55] uppercase tracking-wider block mb-1">
            NHIỆM VỤ TIẾP THEO
          </span>
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-white leading-tight">
            Tìm “{nextTarget.title}”
          </h2>
          <p className="mt-2 text-[#cdbdaf]">
            Đến khu vực <strong className="text-[#fff4dd]">{nextTarget.location}</strong>, đối chiếu bảng thông tin rồi mở máy ảnh quét mã QR để nhận tem số!
          </p>

          <ul className="scan-checks">
            <li>Đúng không gian trưng bày tại Bảo tàng</li>
            <li>Mã QR riêng biệt dán tại chân hiện vật</li>
            <li>Mở khóa tem ngay lập tức vào Bộ sưu tập</li>
          </ul>

          <div className="pt-2">
            <button
              onClick={onOpenScanner}
              className="gf-button text-xs py-3 px-5 flex items-center gap-2 w-max"
            >
              <QrCode className="w-4 h-4" />
              <span>Quét mã QR hiện vật này</span>
            </button>
          </div>
        </div>
      </section>

      {/* 9 STAMPS ALBUM */}
      <section className="space-y-6">
        <div className="flex items-center justify-between border-b border-[#765d55]/60 pb-3">
          <div>
            <span className="text-xs font-bold text-[#e6ba55] uppercase tracking-widest block">
              SỔ TEM QUỐC BẢO
            </span>
            <h2 className="text-2xl font-serif font-bold text-white mt-0.5">
              Danh mục 09 Bảo vật Quốc gia
            </h2>
          </div>
          <span className="text-xs text-[#cdbdaf]">Chạm vào tem để xem chi tiết</span>
        </div>

        <div className="album">
          {artifacts.map((art) => (
            <div
              key={art.id}
              onClick={() => onOpenArtifact(art)}
              className={`album-item ${!art.unlocked ? 'locked' : ''}`}
            >
              <div className="thumb">
                <img
                  src={art.stamp}
                  alt={art.title}
                  className={`w-full h-full object-contain ${
                    !art.unlocked ? 'grayscale-[80%] opacity-80' : ''
                  }`}
                />
              </div>

              <div className="album-copy">
                <div>
                  <span className="index">0{art.id + 1} / QUỐC BẢO</span>
                  <h3 className="line-clamp-1">{art.title}</h3>
                  <p className="line-clamp-1">{art.author} · {art.material}</p>
                </div>

                <div>
                  <span className="status">
                    {art.unlocked ? '✓ Đã nhận qua QR' : 'Chưa nhận'}
                  </span>
                  <span className="view-detail block hover:underline">
                    Xem chi tiết →
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 4-STEPS QR GUIDE */}
      <section className="relative rounded-3xl overflow-hidden border border-[#d2af66]/40 bg-gradient-to-b from-[#24151e]/95 via-[#1c1117]/95 to-[#150a11]/95 p-5 sm:p-8 lg:p-10 shadow-2xl space-y-6 sm:space-y-8">
        {/* Ambient glow decoration */}
        <div
          className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-[#e6ba55]/10 blur-3xl pointer-events-none"
          aria-hidden="true"
        />
        <div
          className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full bg-[#a50021]/15 blur-3xl pointer-events-none"
          aria-hidden="true"
        />

        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto space-y-2 relative z-10">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#e6ba55]/15 border border-[#e6ba55]/40 text-[#fef08a] text-[11px] font-bold uppercase tracking-widest">
            <Compass className="w-3.5 h-3.5 text-[#f59e0b]" />
            <span>Hướng dẫn tham quan thực địa</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-black text-white leading-tight">
            04 bước thu thập tem tại Bảo tàng
          </h2>
          <p className="text-xs sm:text-sm text-[#cdbdaf] max-w-xl mx-auto leading-relaxed">
            Khám phá trực tiếp tại các phòng trưng bày của Bảo tàng Mỹ thuật Việt Nam (66 Nguyễn Thái Học, Ba Đình, Hà Nội).
          </p>
        </div>

        {/* Responsive Steps Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3.5 sm:gap-4 lg:gap-5 relative z-10">
          {GUIDE_STEPS.map((s) => {
            const IconComp = s.icon;
            return (
              <div
                key={s.step}
                className="relative group rounded-2xl bg-[#2b1723]/80 hover:bg-[#341b2b]/95 border border-[#755d55]/60 hover:border-[#e6ba55] p-4 sm:p-5 flex flex-col justify-between transition-all duration-300 shadow-md hover:shadow-xl hover:-translate-y-0.5"
              >
                <div className="space-y-3">
                  {/* Step header: Badge + Phase + Icon */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#f59e0b] to-[#d97706] text-[#2a1708] font-mono font-black text-xs flex items-center justify-center shadow-md">
                        {s.step}
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#e6ba55]">
                        {s.phase}
                      </span>
                    </div>

                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center border shadow-inner transition-transform group-hover:scale-110 ${s.iconBg}`}>
                      <IconComp className={`w-4 h-4 ${s.iconColor}`} />
                    </div>
                  </div>

                  {/* Step Title */}
                  <h3 className="text-sm sm:text-base font-serif font-bold text-white leading-snug">
                    {s.title}
                  </h3>

                  {/* Step Description */}
                  <p className="text-xs text-[#fff1dc]/85 leading-relaxed">
                    {s.desc}
                  </p>
                </div>

                {/* Step Footer: Tag & Optional Action */}
                <div className="pt-3.5 mt-2 border-t border-white/10 flex items-center justify-between flex-wrap gap-2">
                  <span className="text-[10px] sm:text-[11px] font-medium px-2 py-0.5 rounded-full bg-black/40 border border-white/10 text-[#f5d0c5]/90 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#e6ba55]" />
                    {s.badge}
                  </span>

                  {s.hasScannerCta && (
                    <button
                      onClick={onOpenScanner}
                      className="inline-flex items-center gap-1 text-[11px] font-bold text-[#fef08a] hover:text-white transition-colors ml-auto group-hover:underline active:scale-95"
                    >
                      <span>Quét ngay</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer Reassurance & Action Strip */}
        <div className="rounded-2xl bg-black/40 border border-[#e6ba55]/30 p-4 sm:p-5 flex flex-col sm:flex-row items-center justify-between gap-4 relative z-10">
          <div className="flex items-start gap-3 w-full sm:w-auto">
            <div className="w-9 h-9 rounded-xl bg-[#e6ba55]/15 border border-[#e6ba55]/40 flex items-center justify-center text-[#f59e0b] shrink-0 mt-0.5">
              <Compass className="w-4 h-4" />
            </div>
            <div className="space-y-0.5 text-left">
              <h4 className="text-xs sm:text-sm font-bold text-[#fff4dd]">
                Bảo tàng mở cửa hàng ngày: 08:30 – 17:00
              </h4>
              <p className="text-[11px] sm:text-xs text-[#cdbdaf] leading-relaxed">
                Không cần cài đặt app. Vui lòng cho phép trình duyệt truy cập máy ảnh để quét mã QR tại chân hiện vật.
              </p>
            </div>
          </div>

          <button
            onClick={onOpenScanner}
            className="gf-button text-xs py-2.5 px-5 flex items-center gap-2 shrink-0 w-full sm:w-auto justify-center shadow-lg"
          >
            <QrCode className="w-4 h-4" />
            <span>Mở camera quét QR</span>
          </button>
        </div>
      </section>
    </div>
  );
};
